import {RouteReuseStrategy, RouterModule, Routes} from '@angular/router';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {AuthModule} from './auth/auth.module';
import {NgProgressModule} from 'ngx-progressbar';
import {UtilsService} from '@shared/utils/util';
import {SharedModule} from '@shared/shared.module';
import {StartupService} from './startup.service';
import {ModuleSchemaService} from './main/modules/services/moduleSchema.service';
import {environment} from '../environments/environment';
import {DynamicUtilService} from './services/dynamic-util.service';
import {ModuleSchemaStoreService} from './services/module-schema-store.service';
import {MediaService} from './services/media.service';
import {NgProgressHttpModule} from 'ngx-progressbar/http';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {GeneralService} from './services/general.service';
import {Language} from '@shared/enums/language.enum';
import {ErrorsHandler} from './auth/services/error-handler';
import {DefaultErrorReporter, ErrorReporter} from './shared/error-reporting/error-reporter';
import {NoopTelemetrySink, TelemetrySink} from './shared/error-reporting/telemetry';
import {TelemetryService} from './shared/error-reporting/telemetry.service';
import {CustomReuseStrategy} from './custom-reuse-strategy';


export function startupServiceFactory(startupService: StartupService, generalService: GeneralService): () => any {
  return async () => {
    return await startupService.loadModulesSchema();
  };
}

export function telemetryInitializerFactory(telemetry: TelemetryService): () => void {
  return () => telemetry.start();
}

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, window.location.origin + '/assets/i18n/', '.json');
}

export const routesRoot: Routes = [
  {path: 'login', loadChildren: () => import('./login/login.module').then(m => m.LoginModule)},
  {
    path: 'modules', loadChildren: () => import('./main/main.module').then(m => {
      // console.log(m.MainModule.name);
      return m.MainModule;
    })
  },
  {path: '**', redirectTo: 'modules'}
];

@NgModule({
  providers: [
    StartupService,
    ModuleSchemaService,
    ModuleSchemaStoreService,
    DynamicUtilService,
    MediaService,
    {
      provide: 'BASE_API_URL',
      useValue: environment.baseUrl
    },
    {
      provide: APP_INITIALIZER,
      useFactory: startupServiceFactory,
      deps: [StartupService, ModuleSchemaService, GeneralService],
      multi: true
    },
    {
      provide: 'MODULE_UTILS',
      useClass: DynamicUtilService,
    },
    {
      provide: 'MODULE_SCHEMA_STORE',
      useExisting: ModuleSchemaStoreService,
    },
    {
      provide: 'MEDIA_SERVICE',
      useClass: MediaService,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy
    },
    UtilsService,
    {provide: ErrorReporter, useClass: DefaultErrorReporter},
    {provide: ErrorHandler, useClass: ErrorsHandler},
    {provide: TelemetrySink, useClass: NoopTelemetrySink},
    TelemetryService,
    {
      provide: APP_INITIALIZER,
      useFactory: telemetryInitializerFactory,
      deps: [TelemetryService],
      multi: true,
    },
  ],
  declarations: [
    AppComponent
  ],
  imports: [
    AuthModule,
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    SharedModule,
    HttpClientModule,
    NgProgressModule,
    NgProgressHttpModule,
    TranslateModule.forRoot({
      defaultLanguage: 'he',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    RouterModule.forRoot(routesRoot, {enableTracing: false}),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private startupService: StartupService, private translate: TranslateService, private generalService: GeneralService) {
    this.initializeLanguage();
  }

  private initializeLanguage() {
    const browserLang = this.translate.getBrowserLang();
    const usersReferredLanguage = this.generalService.getUsersReferredLanguage();
    const selectedLang = usersReferredLanguage ? usersReferredLanguage : (browserLang ? browserLang : 'he');
    this.translate.setDefaultLang(selectedLang); // Use browser language or fallback to Hebrew
    this.generalService.changeLanguage(selectedLang as Language)
    // this.translate.use(browserLang ? browserLang : 'he');
  }
}
